import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useCreateEvacuationPlan } from "./useEvacuationData";
import { useOrganisation } from "./useOrganisation";
import { EvacuationFormStep, CreateEvacuationPlanInput } from "../types/evacuation";

interface UseCreateEvacuationFormResult {
  name: string;
  setName: (name: string) => void;
  emergencyType: string;
  setEmergencyType: (type: string) => void;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  steps: EvacuationFormStep[];
  addStep: () => void;
  removeStep: (stepId: string | number) => void;
  updateStep: (stepId: string | number, description: string) => void;
  handleSubmit: () => void;
  isSubmitting: boolean;
}

export const useCreateEvacuationForm = (
  onSuccess?: () => void
): UseCreateEvacuationFormResult => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { organisationId } = useOrganisation();
  const { mutate, isLoading: isSubmitting } = useCreateEvacuationPlan();

  const [name, setName] = useState("");
  const [emergencyType, setEmergencyType] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<EvacuationFormStep[]>([
    { id: crypto.randomUUID(), description: "", order: 1 },
  ]);

  const addStep = useCallback(() => {
    const newStep: EvacuationFormStep = {
      id: crypto.randomUUID(),
      description: "",
      order: steps.length + 1,
    };
    setSteps((prev) => [...prev, newStep]);
  }, [steps.length]);

  const removeStep = useCallback((stepId: string | number) => {
    setSteps((prev) => {
      if (prev.length <= 1) return prev;
      return prev
        .filter((step) => step.id !== stepId)
        .map((step, index) => ({ ...step, order: index + 1 }));
    });
  }, []);

  const updateStep = useCallback((stepId: string | number, description: string) => {
    setSteps((prev) =>
      prev.map((step) =>
        step.id === stepId ? { ...step, description } : step
      )
    );
  }, []);

  const handleSubmit = useCallback(() => {
    if (!name || !emergencyType || !organisationId) {
      enqueueSnackbar("Vänligen fyll i alla obligatoriska fält.", {
        variant: "error",
      });
      return;
    }

    const validSteps = steps.filter((step) => step.description.trim() !== "");
    if (validSteps.length === 0) {
      enqueueSnackbar("Lägg till minst ett steg i checklistan.", {
        variant: "error",
      });
      return;
    }

    const evacuationPlan: CreateEvacuationPlanInput = {
      name,
      emergency_type: emergencyType,
      organisation_id: organisationId,
      is_active: isActive,
      steps: validSteps.map((step) => ({
        description: step.description,
        order: step.order,
      })),
    };

    mutate(evacuationPlan, {
      onSuccess: (response) => {
        const planId =
          response?.data?.id ||
          response?.data?.raw?.insertId ||
          response?.id ||
          response?.insertId;

        enqueueSnackbar("Checklista skapad.", {
          variant: "success",
        });

        if (planId) {
          navigate(`/admin/evacuation/details/${planId}`);
        }

        onSuccess?.();
      },
      onError: (error) => {
        enqueueSnackbar("Checklista kunde inte skapas.", {
          variant: "error",
        });
        console.error("Error creating evacuation plan:", error);
      },
    });
  }, [
    name,
    emergencyType,
    organisationId,
    isActive,
    steps,
    mutate,
    navigate,
    enqueueSnackbar,
    onSuccess,
  ]);

  return {
    name,
    setName,
    emergencyType,
    setEmergencyType,
    isActive,
    setIsActive,
    steps,
    addStep,
    removeStep,
    updateStep,
    handleSubmit,
    isSubmitting,
  };
};
